import { computed } from 'vue'
import { useListStore } from '../stores/listStore'
import { useRogue } from '../services/Rogue'
import { useProductStore } from '@/js/stores/productStore'
import { isEnabledCustomProductMainSku, isEnabledCustomProductMember } from '@/js/customProducts/registry'

export function useCustomItemFiltering() {
  const productStore = useProductStore()
  const listStore = useListStore()
  const rogue = useRogue()

  const cart = computed(() => listStore.cart)
  const sfl = computed(() => listStore.sfl)

  const getItemSku = (productId: string) => productStore.products[productId]?.sku

  const isCaveProduct = (sku: string | undefined) => isEnabledCustomProductMember(rogue, 'cave', 'itemSkus', sku)

  const isMassStorageProduct = (sku: string | undefined) =>
    isEnabledCustomProductMember(rogue, 'mass-storage', 'itemSkus', sku) ||
    isEnabledCustomProductMember(rogue, 'monster-mass-storage', 'itemSkus', sku)

  // Unflagged, unlike Cave/MS/MMS.
  const isSlingerXProduct = (sku: string | undefined) => isEnabledCustomProductMember(rogue, 'slinger-x', 'itemSkus', sku)

  // Unflagged, unlike Cave/MS/MMS.
  const isHybridRigProduct = (sku: string | undefined) => isEnabledCustomProductMember(rogue, 'hybrid-rig', 'itemSkus', sku)

  const isMainBundle = (sku: string | undefined): boolean => isEnabledCustomProductMainSku(rogue, sku)

  const isExcluded = (sku: string | undefined) => {
    if (!sku) return false

    return isCaveProduct(sku) || isMassStorageProduct(sku) || isSlingerXProduct(sku) || isHybridRigProduct(sku)
  }

  const customCartItems = computed(() =>
    cart.value.items.filter(item => isMainBundle(getItemSku(String(item.productId))))
  )

  const customSflItems = computed(() =>
    sfl.value.items.filter(item => isMainBundle(getItemSku(String(item.productId))))
  )

  const standardCartItems = computed(() =>
    cart.value.items.filter(item => !isExcluded(getItemSku(String(item.productId))))
  )

  const standardSflItems = computed(() =>
    sfl.value.items.filter(item => !isExcluded(getItemSku(String(item.productId))))
  )

  // Union of every product type that hides bundle siblings behind CustomOrderItem's addon slot
  // (Cave joins via metadata.recipe, Slinger X via groupedProductId) — add new products here to scale.
  const isBundleSiblingProduct = (sku: string | undefined) => isCaveProduct(sku) || isSlingerXProduct(sku)

  const bundleCartBuyRequests = computed(() =>
    cart.value.items.filter(item => isBundleSiblingProduct(getItemSku(String(item.productId))))
  )

  const bundleSflBuyRequests = computed(() =>
    sfl.value.items.filter(item => isBundleSiblingProduct(getItemSku(String(item.productId))))
  )

  return {
    customCartItems,
    customSflItems,
    standardCartItems,
    standardSflItems,
    bundleCartBuyRequests,
    bundleSflBuyRequests,
  }
}